import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { ComponentProps } from "react";

/**
 * CBadge - Shared badge component with color and size variants
 *
 * Color variants:
 * - default, secondary, outline (base)
 * - amber, indigo, indigo-solid, blue, emerald, pink, violet, teal, red, orange, cyan, slate
 *
 * Size variants:
 * - xs: Extra small (matches CActivityList badges)
 * - sm: Small (default)
 * - md: Medium
 * - lg: Large
 */

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded border w-fit whitespace-nowrap shrink-0 transition-colors border-transparent",
  {
    variants: {
      variant: {
        // Base variants
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-transparent bg-destructive text-white",
        outline: "text-foreground border-border",
        // Color variants
        amber:
          "border-transparent bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
        indigo:
          "border-transparent bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
        "indigo-solid":
          "border-transparent bg-indigo-500 text-white dark:bg-indigo-600",
        blue: "border-transparent bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
        emerald:
          "border-transparent bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400",
        pink: "border-transparent bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400",
        violet:
          "border-transparent bg-violet-100 text-violet-600 dark:bg-violet-900/30 dark:text-violet-400",
        teal: "border-transparent bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400",
        red: "border-transparent bg-red-100 text-red-500 dark:bg-red-900/30 dark:text-red-400",
        orange:
          "border-transparent bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
        cyan: "border-transparent bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400",
        slate:
          "border-transparent bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
      },
      size: {
        xs: "text-[10px] px-1.5 py-0.5 font-semibold", // Matches CActivityList badges
        sm: "h-8 text-sm px-3 font-medium", // Matches button sm
        md: "h-9 text-sm px-4 font-medium", // Matches button default
        lg: "h-10 text-sm px-5 font-medium", // Matches button lg
      },
    },
    defaultVariants: {
      variant: "default",
      size: "sm",
    },
  },
);

export interface CBadgeProps
  extends ComponentProps<"span">,
    VariantProps<typeof badgeVariants> {}

const CBadge = ({ className, variant, size, ...props }: CBadgeProps) => {
  return (
    <span
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
};

export default CBadge;
export { CBadge, badgeVariants };
